import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { getAllProducts } from '../API/Crud/Product';
import { getAllServices } from '../API/Crud/Service';
import { getAllCategories } from '../API/Crud/Category';
import { getAllMembres } from '../API/Crud/Membre';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import DashboardTemplate from './template/DashboardTemplate';
import { BiCog, BiGlobe, BiListUl, BiNews, BiUserCircle } from 'react-icons/bi';
import { FaNewspaper } from 'react-icons/fa';
import { getAllNews } from '../API/Crud/News';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [productsCount, setProductsCount] = useState(0);
  const [servicesCount, setServicesCount] = useState(0);
  const [newsCount, setNewsCount] = useState(0);
  const [membersCount, setMembersCount] = useState(0);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      console.error('No token found');
      setError('No token found. Please log in.');
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    async function fetchData() {
      try {
        const productsResponse = await getAllProducts();
        const servicesResponse = await getAllServices();
        const newsResponse = await getAllNews();
        const membersResponse = await getAllMembres();
        setProducts(productsResponse);
        setFilteredProjects(productsResponse); // initial filtered list
        setProductsCount(productsResponse.length);
        setServicesCount(servicesResponse.data.data.length);
        setNewsCount(newsResponse.data.length);
        setMembersCount(membersResponse.data.length);
        setError(null);
      } catch (error) {
        setError(error.message || 'An unknown error occurred');
        toast.error('Failed to load data');
      }
    }

    fetchData();
  }, [token]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSearch = () => {
    const filtered = projects.filter(project =>
      project.nom?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProjects(filtered);
  };

  const barChartData = {
    labels: ['products', 'Services', 'News', 'Members'],
    datasets: [
      {
        label: 'Counts',
        data: [productsCount, servicesCount, newsCount, membersCount],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const pieChartData = {
    labels: ['products', 'Services', 'news', 'Members'],
    datasets: [
      {
        data: [productsCount, servicesCount, newsCount, membersCount],
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#333' },
      },
      title: {
        display: true,
        text: 'Overview of Counts',
        color: '#333',
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#333' },
      },
      title: {
        display: true,
        text: 'Distribution of Entities',
        color: '#333',
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <DashboardTemplate>
      <div className="flex">
        <div className="transition-all duration-500 flex-1" >
          <div className="content p-6">
            {/* Statistiques */}
            <section className="mb-8">
              {/* <h1 className="text-3xl font-semibold text-gray-900 mb-4">Admin Dashboard</h1> */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 shadow-lg rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <BiListUl size={35} color='#F2A549' />
                    <div className="ml-4">
                      <p className="text-lg font-semibold ">{productsCount}</p>
                      <p className="text-black">Products</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 shadow-lg rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <BiCog size={35} color='#F2A549' />
                    <div className="ml-4">
                      <p className={`text-lg font-semibold`}>{servicesCount}</p>
                      <p className="text-black">Services</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 shadow-lg rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <BiNews size={35} color='#F2A549' />
                    <div className="ml-4">
                      <p className="text-lg font-semibold">{newsCount}</p>
                      <p className="text-black">News</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 shadow-lg rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <BiUserCircle size={35} color='#F2A549' />
                    <div className="ml-4">
                      <p className="text-lg font-semibold">{membersCount}</p>
                      <p className="text-black">Membres</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 shadow-lg rounded-lg md:col-span-1">
                  <h2 className="text-xl font-semibold mb-4">Projects Overview</h2>
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
                <div className="bg-white p-6 shadow-lg rounded-lg md:col-span-1">
                  <h2 className="text-xl font-semibold mb-4">Distribution of Entities</h2>
                  <div className="w-full h-80">
                    <Pie data={pieChartData} options={pieChartOptions} />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </DashboardTemplate>
  );
}

export default Dashboard;
